import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Menu } from 'lucide-react';

interface ChildMobileHeaderProps {
  /** Opens the mobile sidebar drawer (wired by Layout). */
  onMobileSidebarToggle?: () => void;
  /** Show the hamburger (only when the user has a sidebar to open). */
  showSidebarToggle?: boolean;
  /**
   * Solid variant: a real white top bar (for content pages that start with
   * plain content). Default is the transparent floating strip that overlays a
   * cover photo, e.g. the social profile.
   */
  solid?: boolean;
}

/**
 * ChildMobileHeader — minimal, app-style header for child pages (e.g. a social
 * profile). Instead of a solid bar, it floats transparently over the page so
 * the content (like a cover photo) runs edge-to-edge, Instagram / Pinterest
 * style. Only the two icon chips are interactive — the rest of the strip lets
 * taps fall through to the page beneath.
 *
 * Left: a modern back button. Right: the same hamburger used elsewhere, which
 * opens the mobile sidebar. Mobile only — desktop keeps the Navbar.
 */
const ChildMobileHeader: React.FC<ChildMobileHeaderProps> = ({
  onMobileSidebarToggle,
  showSidebarToggle = true,
  solid = false,
}) => {
  const navigate = useNavigate();

  const goBack = () => {
    // Fall back to Home when there's no in-app history to pop (e.g. deep link).
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const chip =
    'pointer-events-auto w-10 h-10 flex items-center justify-center rounded-full ' +
    'text-gray-900 active:scale-95 transition-transform duration-200 ' +
    (solid
      ? 'hover:bg-gray-100'
      : 'bg-white/80 backdrop-blur-md shadow-sm ring-1 ring-black/5');

  return (
    <div
      className={
        'md:hidden fixed top-0 inset-x-0 z-50 pt-[env(safe-area-inset-top)] ' +
        (solid
          ? 'bg-white/95 backdrop-blur-lg border-b border-gray-200/60 shadow-sm'
          : 'pointer-events-none')
      }
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <button onClick={goBack} aria-label="Go back" className={chip}>
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>

        {showSidebarToggle && (
          <button
            onClick={onMobileSidebarToggle}
            aria-label="Open menu"
            className={chip}
          >
            <Menu size={20} strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ChildMobileHeader;
